/**
 * Widget Agenda - Affiche les événements groupés par date avec filtres
 */

import { html, unsafeCSS, CSSResultGroup } from "lit";
import { customElement, state } from "lit/decorators.js";
import { ref, createRef, Ref } from "lit/directives/ref.js";
import { BaseWidget } from "../components/BaseWidget";
import "../components/FilterPanel";
import "../components/WidgetHeader";
import "../components/WidgetLayout";
import "../components/Icon";
import "../components/ListItemCard";
import { ObjetTouristique } from "../types/apidae.d";
import { getFieldData, getFieldIconName } from "../utils/itemFieldUtils";
import {
  getOpenSystemWidgetFromItem,
  isOpenSystemConfigured,
  getEllohaWidgetFromItem,
  isEllohaConfigured,
} from "../utils/displayedFieldUtils";
import { getPriceFromBatch } from "../services/EllohaService";
import { DisplayedFieldId, WidgetLanguage } from "../types/widgets.d";
import agendaWidgetStyles from "../styles/agenda-widget.css?inline";
import { formatDate, getImageUrl, getLabel, t } from "../utils/widgetsUtils";

@customElement("apidae-widget-agenda")
export class AgendaWidget extends BaseWidget {
  static styles: CSSResultGroup = [
    BaseWidget.sharedStyles,
    unsafeCSS(agendaWidgetStyles),
  ];

  @state()
  private groupedByDate: Map<string, ObjetTouristique[]> = new Map();

  @state()
  private failedImages: Set<number> = new Set();

  @state()
  private loadedImages: Set<number> = new Set();

  @state()
  private ellohaPrices: Map<string, number> = new Map();

  private sentinelRef: Ref<HTMLDivElement> = createRef();
  private intersectionObserver?: IntersectionObserver;

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  firstUpdated() {
    // Configurer l'IntersectionObserver pour le scroll infini
    this.setupIntersectionObserver();
  }

  updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);

    if (changedProperties.has("items")) {
      this.loadEllohaPrices();
    }

    // Reconfigurer l'observer si le sentinel change
    if (changedProperties.has("items") || changedProperties.has("hasMore")) {
      requestAnimationFrame(() => {
        this.setupIntersectionObserver();
      });
    }

    if (
      this.changedNotNull(changedProperties, "items") ||
      changedProperties.has("apiFilters") ||
      changedProperties.has("activeFilters") ||
      changedProperties.has("groupedByDateFromApi")
    ) {
      if (this.groupedByDateFromApi != null) {
        this.groupedByDate = this.groupedByDateFromApi;
      } else {
        this.groupByDate();
      }
    }

    if (
      this.changedNotNull(changedProperties, "currentPage") &&
      this.currentPage === 1
    ) {
      this.resetScroll();
    }
  }

  private static cultureFromLanguage(lang: WidgetLanguage): string {
    const map: Record<WidgetLanguage, string> = {
      [WidgetLanguage.FR]: "fr-FR",
      [WidgetLanguage.EN]: "en-GB",
      [WidgetLanguage.ES]: "es-ES",
      [WidgetLanguage.IT]: "it-IT",
      [WidgetLanguage.DE]: "de-DE",
      [WidgetLanguage.NL]: "nl-NL",
      [WidgetLanguage.RU]: "ru-RU",
      [WidgetLanguage.ZH]: "zh-CN",
      [WidgetLanguage.PT_BR]: "pt-BR",
      [WidgetLanguage.JA]: "ja-JP",
      [WidgetLanguage.KO]: "ko-KR",
    };
    return map[lang] ?? "fr-FR";
  }

  private async loadEllohaPrices() {
    const displayedFields = this.config?.contentCustomization?.displayedFields;
    if (!isEllohaConfigured(displayedFields) || !this.items?.length) {
      this.ellohaPrices = new Map();
      return;
    }
    const field = displayedFields?.find(
      (f) => f.id === DisplayedFieldId.DISPONIBILITES_ELLOHA,
    );
    const data = field?.data as
      | { idBookingEngine?: string; ellohaClientId?: string }
      | undefined;
    const idBookingEngine = data?.idBookingEngine;
    if (!idBookingEngine) return;

    const idProducts: string[] = [];
    for (const item of this.items) {
      const elloha = getEllohaWidgetFromItem(item);
      if (elloha?.idProduct) idProducts.push(elloha.idProduct);
    }
    if (idProducts.length === 0) {
      this.ellohaPrices = new Map();
      return;
    }

    const culture = AgendaWidget.cultureFromLanguage(this.language);
    const ellohaClientId = data?.ellohaClientId;
    const map = await getPriceFromBatch(
      idBookingEngine,
      idProducts,
      culture,
      ellohaClientId,
    );
    this.ellohaPrices = new Map(map);
    this.requestUpdate();
  }

  private groupByDate() {
    const groups = new Map<
      string,
      { sortKey: string; items: ObjetTouristique[] }
    >();
    const currentYear = new Date().getFullYear();

    this.items.forEach((item) => {
      const periode = item.ouverture?.periodesOuvertures?.[0];
      if (periode?.dateDebut) {
        const dateDebut = new Date(periode.dateDebut);

        // Si tousLesAns est true, utiliser l'année en cours, sinon utiliser l'année de dateDebut
        const year = periode.tousLesAns ? currentYear : dateDebut.getFullYear();
        const month = dateDebut.getMonth() + 1; // getMonth() retourne 0-11
        const day = dateDebut.getDate();

        // Clé de groupement : jour-mois seulement (MM-DD)
        const groupKey = `${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

        // Clé de tri : date complète avec l'année appropriée (YYYY-MM-DD)
        const sortKey = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

        if (!groups.has(groupKey)) {
          groups.set(groupKey, { sortKey, items: [] });
        }
        groups.get(groupKey)!.items.push(item);
      }
    });

    // Trier par date (en utilisant sortKey) et convertir en Map avec la clé de groupement
    const sortedEntries = Array.from(groups.entries()).sort((a, b) =>
      a[1].sortKey.localeCompare(b[1].sortKey),
    );

    // Convertir en Map avec la clé de tri comme clé (pour l'affichage avec formatDateHeader)
    this.groupedByDate = new Map(
      sortedEntries.map(([, { sortKey, items }]) => [sortKey, items]),
    );
  }

  protected async handleFilterChange(e: CustomEvent) {
    super.handleFilterChange(e);

    // Réinitialiser le scroll en haut pour éviter que l'IntersectionObserver déclenche un chargement immédiat
    this.resetScroll();
  }

  protected async handleClearFilters() {
    super.handleClearFilters();

    // Réinitialiser le scroll en haut pour éviter que l'IntersectionObserver déclenche un chargement immédiat
    this.resetScroll();
  }

  private resetScroll() {
    requestAnimationFrame(() => {
      const scrollContainer = this.shadowRoot?.querySelector(
        ".agenda-main",
      ) as HTMLElement;
      if (scrollContainer) {
        scrollContainer.scrollTop = 0;
      }
      // Réinitialiser l'IntersectionObserver pour qu'il ne déclenche pas immédiatement
      this.setupIntersectionObserver();
    });
  }

  private setupIntersectionObserver() {
    // Détruire l'ancien observer s'il existe
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    // Si pas de sentinel ou pas de données à charger, ne pas créer d'observer
    if (!this.sentinelRef.value || !this.hasMore) {
      return;
    }

    // Trouver le conteneur scrollable
    const scrollContainer = this.shadowRoot?.querySelector(
      ".agenda-main",
    ) as HTMLElement;
    if (!scrollContainer) {
      return;
    }

    // Créer un nouvel observer avec le conteneur scrollable comme root
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && this.hasMore && !this.loading) {
            if (this.isChildWidget) {
              this.dispatchEvent(
                new CustomEvent("child-context-change", {
                  detail: {
                    source: "agenda",
                    loadNextPage: true,
                  },
                  bubbles: true,
                  composed: true,
                }),
              );
            } else {
              this.loadNextPage(false);
            }
          }
        });
      },
      {
        root: scrollContainer, // Utiliser le conteneur scrollable comme root
        rootMargin: "100px", // Charger quand on est à 100px du bas
      },
    );

    this.intersectionObserver.observe(this.sentinelRef.value);
  }

  private formatDateHeader(dateStr: string): string {
    // Formater la date sous la forme "Jour x mois" (ex: "Lundi 15 janvier")
    const formatted = formatDate(dateStr, this.language, {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
    // Capitaliser la première lettre
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }

  protected renderWidget() {
    if (this.loading) {
      return html`
        <div class="loading">
          <div class="loading-spinner"></div>
          <p>${t("loading", this.language)}</p>
        </div>
      `;
    }

    if (this.error) {
      return this.renderErrorScreen();
    }

    const hasFilterFields =
      this.config?.contentCustomization?.filterFields &&
      this.config.contentCustomization.filterFields.length > 0;
    const showFiltersConfig =
      hasFilterFields &&
      (this.config?.templateSpecificConfig as any)?.config?.showFilters !==
        false;
    const showFilters = showFiltersConfig && this.filtersVisible;
    const layoutFraction = this.getLayoutFraction();

    return html`
      <apidae-widget-layout
        .config=${{
          fraction: this.hideHeaderAndFilters ? "full" : layoutFraction,
          showFilters: this.hideHeaderAndFilters ? false : showFilters,
          headerSlot: this.hideHeaderAndFilters ? undefined : "header",
          filtersSlot: this.hideHeaderAndFilters
            ? undefined
            : showFilters
              ? "filters"
              : undefined,
          agendaSlot: "agenda",
        }}
      >
        ${!this.hideHeaderAndFilters
          ? html`
              <div slot="header">
                <apidae-widget-header
                  .config=${{
                    widgetName: this.config?.name,
                    logo: this.config?.visualCustomization?.logo,
                    logoUrl: this.config?.visualCustomization?.logoUrl,
                    language: this.language,
                    showFiltersButton: showFiltersConfig,
                    showClearButton: showFiltersConfig,
                    hasActiveFilters: this.hasActiveFilters(),
                    showSwitcher: false,
                    filtersVisible: this.filtersVisible,
                    resultsCount: this.getResultsCountForDisplay(),
                    primaryColor:
                      this.config?.visualCustomization?.colors?.primary,
                    secondaryColor:
                      this.config?.visualCustomization?.colors?.secondary,
                  }}
                  @toggle-filters=${this.handleToggleFilters}
                  @clear-filters=${this.handleClearFilters}
                ></apidae-widget-header>
              </div>

              ${showFilters
                ? html`
                    <div slot="filters" style="flex: 1;">
                      <apidae-filter-panel
                        .filterGroups=${this.filterGroups}
                        .language=${this.language}
                        .activeFilters=${this.activeFilters}
                        .dateRange=${this.dateRange}
                        .loadingMoreFacetId=${this.loadingMoreFacetId}
                        @filter-change=${this.handleFilterChange}
                        @load-more-facet=${this.handleLoadMoreFacet}
                      ></apidae-filter-panel>
                    </div>
                  `
                : ""}
            `
          : ""}

        <div slot="agenda" class="agenda-main">
          ${this.items.length === 0 && !this.hideEmptyState
            ? html`
                <div class="empty-state">
                  <div class="empty-state-title">
                    ${t("noResultsFound", this.language) ||
                    "Aucun résultat trouvé."}
                  </div>
                  <div class="empty-state-subtitle">
                    ${t("adjustSearchWithFilters", this.language) ||
                    "Ajustez votre recherche grâce aux filtres."}
                  </div>
                </div>
              `
            : this.groupedByDate.size === 0 && !this.hideEmptyState
              ? html`
                  <div class="no-dates-message">
                    ${t("noEventsWithDate", this.language) ||
                    "Aucun événement avec date disponible"}
                  </div>
                `
              : html`
                  <div class="agenda-container">
                    ${Array.from(this.groupedByDate.entries()).map(
                      ([date, events]) => this.renderDateGroup(date, events),
                    )}
                    ${this.hasMore
                      ? html`<div
                          ${ref(this.sentinelRef)}
                          style="height: 1px;"
                        ></div>`
                      : ""}
                  </div>
                `}
        </div>
      </apidae-widget-layout>
    `;
  }

  private renderDateGroup(dateStr: string, events: ObjetTouristique[]) {
    const dateFormatted = this.formatDateHeader(dateStr);

    return html`
      <div class="date-group">
        <div class="date-header-wrapper">
          <h3 class="date-header">${dateFormatted}</h3>
        </div>
        <div class="list-grid">
          ${events.map((event) => this.renderListItem(event))}
        </div>
      </div>
    `;
  }

  private renderListItem(item: ObjetTouristique) {
    const imageUrl = getImageUrl(item, "diaporama", this.language);
    const nom = getLabel(item.nom, this.language);
    const displayedFields =
      this.config?.contentCustomization?.displayedFieldsPreview || [];
    const hasOpenSystem =
      isOpenSystemConfigured(
        this.config?.contentCustomization?.displayedFields,
      ) && !!getOpenSystemWidgetFromItem(item)?.ui;
    const ellohaFromItem = getEllohaWidgetFromItem(item);
    const hasElloha =
      isEllohaConfigured(this.config?.contentCustomization?.displayedFields) &&
      !!ellohaFromItem?.idProduct;
    const ellohaPriceFrom =
      hasElloha && ellohaFromItem?.idProduct
        ? this.ellohaPrices.get(ellohaFromItem.idProduct) ?? null
        : null;

    return html`
      <apidae-list-item-card
        data-item-id="${item.id}"
        .item=${item}
        .imageUrl=${imageUrl}
        .nom=${nom}
        .displayedFields=${displayedFields}
        .hasOpenSystem=${hasOpenSystem}
        .hasElloha=${hasElloha}
        .ellohaPriceFrom=${ellohaPriceFrom}
        .typography=${this.config?.visualCustomization?.typography}
        .language=${this.language}
        .apiFilters=${this.apiFilters}
        .selectedItemId=${this.selectedItemId ?? null}
        .failedImages=${this.failedImages}
        .loadedImages=${this.loadedImages}
        .t=${(key: string) => t(key, this.language)}
        .getFieldData=${(
          it: ObjetTouristique,
          fieldId: string,
          _apiFilters: unknown,
          _language: string,
        ) => this.getFieldData(it, fieldId)}
        .getFieldIconName=${getFieldIconName}
        .onItemClick=${(clickedItem: ObjetTouristique) => {
          if (this.disableDetailView) {
            this.dispatchEvent(
              new CustomEvent("item-click", {
                detail: { item: clickedItem },
                bubbles: true,
                composed: true,
              }),
            );
          } else {
            this.handleItemClick(clickedItem);
          }
        }}
        .onImageLoad=${(id: number) => {
          this.loadedImages.add(id);
          this.requestUpdate();
        }}
        .onImageError=${(id: number) => {
          this.failedImages.add(id);
          this.requestUpdate();
        }}
      ></apidae-list-item-card>
    `;
  }

  private getFieldData(
    item: ObjetTouristique,
    fieldId: string,
  ): { value: string; url?: string } | null {
    return getFieldData(item, fieldId, {
      language: this.language,
      getLabel: (libelle) => getLabel(libelle, this.language),
      formatDate: (dateStr, options) =>
        formatDate(dateStr, this.language, options),
      t: (key) => t(key, this.language),
    });
  }

  render() {
    return html`
      <div class="widget-container">
        <div class="widget-content">${this.renderWidget()}</div>
        ${this.renderDetailView()} ${this.renderToast()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "apidae-widget-agenda": AgendaWidget;
  }
}
